import { FC, useState } from 'react';
import classNames from 'classnames';
import { AddToCartOrList } from '../AddToCartOrList/AddToCartOrList';
import { TargetSelectionListDropdown } from '../TargetSelectionListDropdown/TargetSelectionListDropdown';
import { AvailablePricesSelector } from '../AvailablePricesSelector/AvailablePricesSelector';
import './AddProduct.scss';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { Article, Price, SubscriptionPeriodType } from '../../../../models/Article';
import { ProductMetaData } from '../../../../models/Product';
import { useOrderLists } from '../useOrderLists';
import { showNotification, TYPE_ERROR } from '../../../store/notifications/actions';
import { ButtonSize, ButtonTheme } from '../Button';
import { useTarget } from '../useTarget';
import { UnavailableSelector } from '../UnavailableSelector/UnavailableSelector';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { LoanFormat, mapLoanFormat } from '../../../../models/LoanFormat';
import { useSubscriptionStartOptions } from '../../../api/subscriptions/useSubscriptionStartOptions';
import { UpdateProductOptions } from '../../../store/products/actionsCart';
import { SubscriptionPeriodPopup } from '../SubscriptionStartPopup';
import { useFetchSubscriptions } from '../../../api/subscriptions/useFetchSubscriptions';
import { THEME_PRIMARY, themeColor } from '../../../styles/basics/colors';
import { Trans, useTranslation } from 'react-i18next';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';

/**
 * Props for <AddProductInternal>.
 */
interface AddProductProps {
    /**
     * The associated product.
     */
    readonly product: ProductMetaData & Article;

    /**
     * Callback for adding a price to a specific target.
     */
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;

    /**
     * Callback for selecting a price.
     */
    readonly onSelectPrice: (price: Price, subscriptionPeriodType?: SubscriptionPeriodType) => Promise<void>;

    /**
     * Indicates whether only the currently selected price should be shown as selected.
     */
    readonly showSelectedPriceOnly: boolean;

    /**
     * Indicates whether selecting a price is allowed.
     */
    readonly allowSelection: boolean;

    readonly onHideDetails?: () => void;

    readonly size?: ButtonSize;
    readonly theme?: ButtonTheme;
}

const StSubscriptionPeriodText = styled.p`
    margin: 9px 0 0;
    font-size: 11px;
`;

const StEditButton = styled.button`
    color: ${themeColor(THEME_PRIMARY)};
    margin-left: 3px;
`;

export const AddProduct: FC<AddProductProps> = ({
    product,
    onAddPrice,
    onSelectPrice,
    showSelectedPriceOnly,
    allowSelection,
    onHideDetails,
    size,
    theme
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const isProductAvailable =
        product.availablePrices &&
        product.availablePrices.length > 0 &&
        product.salesStatus &&
        product.salesStatus.key !== 'COMING_SOON';
    const isComingSoon = product.salesStatus && product.salesStatus.key === 'COMING_SOON';

    const { target } = useTarget();
    const showSelectionListDropdown = target === ListActionTarget.List;
    const showAddToCartOrListButtons = !showSelectedPriceOnly || allowSelection;

    const { lendingModel, usageLimit, termLimit } = product.price ?? {};

    const { orderlists, selectedOrderList, createNewList } = useOrderLists();

    const orderlistId = selectedOrderList?.id;
    const { id: productId, title: productTitle } = product;

    const [showPopup, setShowPopup] = useState<'select' | 'edit' | false>(false);
    const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriodType>(SubscriptionPeriodType.CURRENT);
    const [selectedPrice, setSelectedPrice] = useState<Price | undefined>(undefined);

    const { data, isLoading: isSubscriptionStartOptionsLoading } = useSubscriptionStartOptions(
        product.id!,
        !!showPopup
    );
    const { data: subscriptionData } = useFetchSubscriptions(product.id, !!showPopup);

    const futureSubscriptions = subscriptionData?.subscriptions.flatMap(
        subscription => subscription.futureSubscriptions
    );
    const activeSubscriptions = subscriptionData?.subscriptions.flatMap(
        subscription => subscription.activeSubscriptions
    );

    const combineUpdateOptionsWithTargetInfo = (
        options: UpdateProductOptions,
        selectedTarget: ListActionTarget = target
    ): SingleAddOptions | null => {
        if (selectedTarget === ListActionTarget.List) {
            if (!orderlistId) {
                dispatch(showNotification(t('notifications.handleAddPrice.list.error'), TYPE_ERROR));
                return null;
            }

            return { options: options, target: ListActionTarget.List, listId: orderlistId };
        } else {
            return { options: options, target: ListActionTarget.Cart };
        }
    };

    const handlePriceSubmit = async (
        { usageLimit: selectedUsageLimit, termLimit: selectedTermLimit, lendingModel: selectedLendingModel }: Price,
        useSelectedPeriod: boolean = true
    ) => {
        const subscriptionPeriodOptions =
            mapLoanFormat(product.loanFormat) === LoanFormat.ePress
                ? {
                      sourceSubscriptionPeriodType: product?.subscriptionPeriod?.type ?? selectedPeriod,
                      targetSubscriptionPeriodType: useSelectedPeriod
                          ? selectedPeriod
                          : product?.subscriptionPeriod?.type
                  }
                : {};

        const options: UpdateProductOptions = {
            ...subscriptionPeriodOptions,
            id: productId,
            title: productTitle,
            price: {
                usageLimit: selectedUsageLimit,
                termLimit: selectedTermLimit,
                lendingModel: selectedLendingModel
            },
            sourceUsageCondition: {
                lendingModel,
                usageLimit,
                termLimit
            },
            sourceId: orderlistId
        };
        const addOptions = combineUpdateOptionsWithTargetInfo(options);

        if (!addOptions) {
            return;
        }
        await onAddPrice(addOptions);
    };

    const handleAddPrice = async (price: Price) => {
        if (mapLoanFormat(product.loanFormat) === LoanFormat.ePress) {
            if (product.subscriptionPeriod) {
                await handlePriceSubmit(price, false);
            } else {
                setSelectedPrice(price);
                setShowPopup('select');
            }
        } else {
            await handlePriceSubmit(price);
        }
    };

    const handleSubscriptionPeriodPopupConfirm = async () => {
        const isEditPopup = showPopup === 'edit';
        if (!selectedPrice) {
            return;
        }

        switch (showPopup) {
            case 'select':
                await handlePriceSubmit(selectedPrice);
                break;
            case 'edit':
                await onSelectPrice(selectedPrice, selectedPeriod);
                break;
        }

        setShowPopup(false);
        setSelectedPrice(undefined);

        if (isEditPopup) {
            dispatch(showNotification(t('notifications.changeSubscriptionPeriod.message')));
        }
    };

    const openEditSubscriptionPeriodPopup = () => {
        setSelectedPrice(product.price);
        setSelectedPeriod(product.subscriptionPeriod?.type!);
        setShowPopup('edit');
    };

    const handleAddUnavailableProduct = async () => {
        const options = {
            id: productId,
            title: productTitle
        };

        const addOptions = combineUpdateOptionsWithTargetInfo(options, ListActionTarget.List);
        if (!addOptions) {
            return;
        }

        await onAddPrice(addOptions);
    };

    if (!isProductAvailable && showSelectedPriceOnly) {
        return <div className={classNames('add-product')} />;
    }

    return (
        <div className={classNames('add-product')}>
            {showAddToCartOrListButtons ? (
                <>
                    <AddToCartOrList
                        disabledAddToList={showSelectedPriceOnly}
                        disabledAddToCart={!isProductAvailable}
                    />
                    {showSelectionListDropdown && !showSelectedPriceOnly ? (
                        <TargetSelectionListDropdown
                            size={size}
                            theme={theme}
                            orderlists={orderlists}
                            selectedOrderlist={selectedOrderList}
                            onCreate={createNewList}
                        />
                    ) : null}
                </>
            ) : null}
            {product.subscriptionPeriod ? (
                <StSubscriptionPeriodText>
                    <Trans
                        i18nKey="components.subscriptionPeriodPopup.subscriptionPeriodText"
                        components={{ span: <span />, strong: <strong /> }}
                        values={{
                            period:
                                product.subscriptionPeriod?.type === SubscriptionPeriodType.CURRENT
                                    ? t('components.subscriptionPeriodPopup.immediately')
                                    : t('components.subscriptionPeriodPopup.nextSubscriptionYear')
                        }}
                    />
                    <StEditButton onClick={openEditSubscriptionPeriodPopup}>
                        <span className="gild">
                            <span className="icon mode-edit"></span>
                        </span>
                    </StEditButton>
                </StSubscriptionPeriodText>
            ) : null}
            {isProductAvailable ? (
                <AvailablePricesSelector
                    product={product}
                    target={target}
                    onAddPrice={handleAddPrice}
                    onSelectPrice={onSelectPrice}
                    showSelectedPrice={showSelectedPriceOnly}
                    // The condition (showSelectedPriceOnly && target === ListActionTarget.List) is to disable "add to list" in the selection list page after switching to "add to list" in the search suggestion
                    disabled={!allowSelection || (showSelectedPriceOnly && target === ListActionTarget.List)}
                />
            ) : (
                <UnavailableSelector target={target} onAddToList={handleAddUnavailableProduct} />
            )}
            {isComingSoon ? (
                <div className="strong">{t('comingSoon')}</div>
            ) : (
                !isProductAvailable && <div className="strong">{t('noLongerForSale')}</div>
            )}

            <SubscriptionPeriodPopup
                product={product}
                showPopup={!!showPopup}
                hidePopup={() => {
                    setShowPopup(false);
                    onHideDetails?.();
                }}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
                selectedPrice={selectedPrice}
                setSelectedPrice={setSelectedPrice}
                subscriptionPeriodPrices={data}
                onConfirm={handleSubscriptionPeriodPopupConfirm}
                activeSubscriptions={activeSubscriptions?.length ?? 0}
                futureSubscriptions={futureSubscriptions?.length ?? 0}
                isLoading={isSubscriptionStartOptionsLoading}
            />
        </div>
    );
};
